import { useCallback, useEffect, useState } from 'react';

import { getInstance as getConfigServiceInstance } from './singleton';
import type { ExtractLeverValueType, LeverConfigPid, LeverKey } from './types';

/**
 * Uses a lever value (including granular breakout) and subscribes to dynamic updates
 */
export function useLeverOverride<T extends LeverKey>(
  leverKey: T
): [
  {
    cached: ExtractLeverValueType<T> | undefined | null;
    fallback: ExtractLeverValueType<T>;
    override: ExtractLeverValueType<T> | undefined | null;
    pid: LeverConfigPid | undefined | null;
    stored: ExtractLeverValueType<T> | undefined | null;
    value: ExtractLeverValueType<T>;
  },
  (a: ExtractLeverValueType<T>) => void,
  VoidFunction
] {
  const configService = getConfigServiceInstance();
  const [leverData, setLeverData] = useState(configService.DEVTOOLS_getLeverValues(leverKey));

  useEffect(() => {
    const unsubscribeFn = configService.subscribeLever(leverKey, () => {
      setLeverData(configService.DEVTOOLS_getLeverValues(leverKey));
    });

    return () => {
      unsubscribeFn();
    };
  }, [leverKey, configService]);

  const setOverride = useCallback(
    (value: ExtractLeverValueType<T>) => {
      configService.DEVTOOLS_setOverride(leverKey, value);
    },
    [configService, leverKey]
  );

  const clearOverride = useCallback(() => {
    configService.DEVTOOLS_setOverride(leverKey, undefined);
  }, [configService, leverKey]);

  return [leverData, setOverride, clearOverride];
}

/**
 * Uses a lever value and subscribes to dynamic updates
 */
export function useLever<T extends LeverKey>(leverKey: T): ExtractLeverValueType<T> {
  const configService = getConfigServiceInstance();
  const [leverValue, setLeverValue] = useState(configService.readLever(leverKey));

  useEffect(() => {
    const unsubscribeFn = configService.subscribeLever(leverKey, () => {
      setLeverValue(configService.readLever(leverKey));
    });

    return () => {
      unsubscribeFn();
    };
  }, [leverKey, configService]);

  return leverValue;
}

/**
 * Performs a locking read on the lever
 */
export function useLockLever<T extends LeverKey>(leverKey: T): ExtractLeverValueType<T> | null {
  const configService = getConfigServiceInstance();
  const [leverValue, setLeverValue] = useState<ExtractLeverValueType<T> | null>(null);

  useEffect(() => {
    let [value, unlockFn] = configService.readLockLever(leverKey);
    let timeout;

    setLeverValue(value);

    const unsubscribeFn = configService.subscribeLever(leverKey, () => {
      unlockFn();
      // Defer subsequent read to avoid deadlock with other consumers
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        [value, unlockFn] = configService.readLockLever(leverKey);
        setLeverValue(value);
      }, 0);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribeFn();
      unlockFn();
    };
  }, [leverKey, configService]);

  return leverValue;
}
